/**
 * Fleet Escalation Service
 *
 * Background service for monitoring fleet notification acknowledgements
 * and triggering escalations per the ISM Code Section 9 requirements.
 *
 * Escalation Policy:
 * - Level 1 (48h): Reminder sent to vessel
 * - Level 2 (7 days): DPA alerted
 * - Level 3 (14 days): Compliance flag raised
 *
 * @see FLEET-DASHBOARD-DESIGN.md Section 3.5 Escalation Logic
 */

import logger from '../utils/logger';

// Escalation thresholds in hours
const ESCALATION_THRESHOLDS = {
  REMINDER: 48,        // 48 hours - Level 1
  DPA_ALERT: 168,      // 7 days - Level 2
  COMPLIANCE_FLAG: 336 // 14 days - Level 3
};

// Check interval (1 hour in milliseconds)
const CHECK_INTERVAL_MS = 60 * 60 * 1000;

class FleetEscalationService {
  constructor() {
    this.checkInterval = null;
    this.isRunning = false;
    this.lastCheckTime = null;
    this.escalationCallbacks = {
      onLevel1: null,  // Reminder
      onLevel2: null,  // DPA Alert
      onLevel3: null   // Compliance Flag
    };
  }

  /**
   * Initialize the escalation service
   * @param {Object} callbacks - Callback functions for escalation events
   */
  initialize(callbacks = {}) {
    this.escalationCallbacks = {
      ...this.escalationCallbacks,
      ...callbacks
    };

    // Start periodic checks
    this.start();

    // Check immediately on startup
    this.checkAllVessels();

    logger.info('[FleetEscalation] Service initialized');
  }

  /**
   * Start the periodic escalation checks
   */
  start() {
    if (this.isRunning) {
      logger.warn('[FleetEscalation] Service already running');
      return;
    }

    this.isRunning = true;
    this.checkInterval = setInterval(() => {
      this.checkAllVessels();
    }, CHECK_INTERVAL_MS);

    // Also check when window gains focus (for immediate feedback)
    window.addEventListener('focus', this.handleWindowFocus);

    logger.info('[FleetEscalation] Service started');
  }

  /**
   * Stop the periodic checks
   */
  stop() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }

    window.removeEventListener('focus', this.handleWindowFocus);
    this.isRunning = false;

    logger.info('[FleetEscalation] Service stopped');
  }

  /**
   * Handle window focus event
   */
  handleWindowFocus = () => {
    // Debounce - don't check more than once per minute
    const now = Date.now();
    if (this.lastCheckTime && (now - this.lastCheckTime) < 60000) {
      return;
    }
    this.checkAllVessels();
  };

  /**
   * Check all vessels for notifications needing escalation
   */
  checkAllVessels() {
    this.lastCheckTime = Date.now();
    const escalated = [];

    try {
      // Get all vessel IDs from localStorage keys
      const vesselIds = this.getVesselIdsFromStorage();

      vesselIds.forEach(vesselId => {
        const updates = this.checkVesselEscalations(vesselId);
        if (updates.length > 0) {
          escalated.push({ vesselId, updates });
        }
      });

      if (escalated.length > 0) {
        logger.info(`[FleetEscalation] Escalated ${escalated.length} vessel(s)`, escalated);

        // Emit event for other parts of the app to react
        window.dispatchEvent(new CustomEvent('sr:escalations:updated', {
          detail: { escalated }
        }));
      }

    } catch (error) {
      logger.error('[FleetEscalation] Error checking escalations:', error);
    }

    return escalated;
  }

  /**
   * Get all vessel IDs that have fleet notifications
   */
  getVesselIdsFromStorage() {
    const vesselIds = new Set();

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('fleet_notifications_')) {
        const vesselId = key.replace('fleet_notifications_', '');
        if (vesselId) {
          vesselIds.add(vesselId);
        }
      }
    }

    return Array.from(vesselIds);
  }

  /**
   * Check a specific vessel for escalation needs
   * @param {string} vesselId - Vessel ID to check
   * @returns {Array} List of escalated notification IDs
   */
  checkVesselEscalations(vesselId) {
    const key = `fleet_notifications_${vesselId}`;
    const updated = [];

    try {
      const raw = localStorage.getItem(key);
      if (!raw) return [];

      const notifications = JSON.parse(raw);
      const now = new Date();
      let hasChanges = false;

      const updatedNotifications = notifications.map(notif => {
        // Skip if already acknowledged or not a shore edit
        if (notif.acknowledgedAt || notif.type !== 'shore_edit') {
          return notif;
        }

        const editedAt = new Date(notif.editedAt);
        const hoursSinceEdit = (now - editedAt) / (1000 * 60 * 60);

        const escalations = [...(notif.escalations || [])];
        let needsUpdate = false;

        // Level 1: 48 hour reminder
        if (hoursSinceEdit >= ESCALATION_THRESHOLDS.REMINDER &&
            !escalations.find(e => e.level === 1)) {
          escalations.push({
            level: 1,
            triggeredAt: now.toISOString(),
            action: 'REMINDER_SENT'
          });
          needsUpdate = true;
          this.triggerCallback('onLevel1', notif, vesselId);
        }

        // Level 2: 7 day DPA alert
        if (hoursSinceEdit >= ESCALATION_THRESHOLDS.DPA_ALERT &&
            !escalations.find(e => e.level === 2)) {
          escalations.push({
            level: 2,
            triggeredAt: now.toISOString(),
            action: 'DPA_ALERTED'
          });
          needsUpdate = true;
          this.triggerCallback('onLevel2', notif, vesselId);
        }

        // Level 3: 14 day compliance flag
        if (hoursSinceEdit >= ESCALATION_THRESHOLDS.COMPLIANCE_FLAG &&
            !escalations.find(e => e.level === 3)) {
          escalations.push({
            level: 3,
            triggeredAt: now.toISOString(),
            action: 'COMPLIANCE_FLAG'
          });
          needsUpdate = true;
          this.triggerCallback('onLevel3', notif, vesselId);
        }

        if (needsUpdate) {
          hasChanges = true;
          updated.push(notif.id);
          return {
            ...notif,
            escalations,
            syncStatus: {
              ...notif.syncStatus,
              status: 'local_only' // Mark for sync
            }
          };
        }

        return notif;
      });

      // Save back if changes were made
      if (hasChanges) {
        localStorage.setItem(key, JSON.stringify(updatedNotifications));

        // Emit event for this vessel
        window.dispatchEvent(new CustomEvent('sr:vessel:escalations', {
          detail: { vesselId, notificationIds: updated }
        }));
      }

    } catch (error) {
      logger.error(`[FleetEscalation] Error checking vessel ${vesselId}:`, error);
    }

    return updated;
  }

  /**
   * Trigger an escalation callback
   */
  triggerCallback(callbackName, notification, vesselId) {
    const callback = this.escalationCallbacks[callbackName];
    if (callback && typeof callback === 'function') {
      try {
        callback(notification, vesselId);
      } catch (error) {
        logger.error(`[FleetEscalation] Error in ${callbackName} callback:`, error);
      }
    }
  }

  /**
   * Get escalation status summary across all vessels
   */
  getEscalationSummary() {
    const summary = {
      level1Count: 0,  // Reminders sent
      level2Count: 0,  // DPA alerts
      level3Count: 0,  // Compliance flags
      unacknowledgedTotal: 0,
      byVessel: {}
    };

    try {
      const vesselIds = this.getVesselIdsFromStorage();

      vesselIds.forEach(vesselId => {
        const key = `fleet_notifications_${vesselId}`;
        const raw = localStorage.getItem(key);
        if (!raw) return;

        const notifications = JSON.parse(raw);
        const vesselSummary = {
          level1: 0,
          level2: 0,
          level3: 0,
          unacknowledged: 0
        };

        notifications.forEach(notif => {
          if (notif.acknowledgedAt || notif.type !== 'shore_edit') return;

          vesselSummary.unacknowledged++;
          summary.unacknowledgedTotal++;

          const maxLevel = Math.max(...(notif.escalations || []).map(e => e.level), 0);
          if (maxLevel >= 1) { vesselSummary.level1++; summary.level1Count++; }
          if (maxLevel >= 2) { vesselSummary.level2++; summary.level2Count++; }
          if (maxLevel >= 3) { vesselSummary.level3++; summary.level3Count++; }
        });

        if (vesselSummary.unacknowledged > 0) {
          summary.byVessel[vesselId] = vesselSummary;
        }
      });

    } catch (error) {
      logger.error('[FleetEscalation] Error getting summary:', error);
    }

    return summary;
  }

  /**
   * Force immediate check (for testing or manual trigger)
   */
  forceCheck() {
    return this.checkAllVessels();
  }
}

// Singleton instance
const fleetEscalationService = new FleetEscalationService();

export { ESCALATION_THRESHOLDS };
export default fleetEscalationService;
